import * as React from 'react';
import propz from 'propz';
import { FunctionComponent } from 'react';
import { getPersonListSortedByName } from '../../helpers/table/table';
import './SchoolTeamPlayersSummary.css';

interface Props {
  players: any[];
  sports: any[];
}

function isPlayerAvailableForSport(player: any, sportId: string): boolean {
  const sportIds = Array.isArray(player.sportIds) ? player.sportIds.map(String) : [];
  return sportIds.length === 0 || sportIds.includes(String(sportId));
}

export const SchoolTeamPlayersSummary: FunctionComponent<Props> = ({ players, sports }) => {
  const sortedPlayers: any[] = getPersonListSortedByName(players);

  return (
    <div className="teamRoster">
      {sports.length > 0 && (
        <section className="teamRoster__sports" aria-label="Sport assignment status">
          <div>
            <strong>Sport assignments</strong>
            <span>Positions and match roles are managed separately for each sport.</span>
          </div>
          <ul>
            {sports.map(sport => {
              const sportId = String(sport.id || sport._id || '');
              const availablePlayers = players.filter(player => isPlayerAvailableForSport(player, sportId));
              const positionedPlayers = availablePlayers.filter(
                player =>
                  (player.positionsBySport || []).some(
                    assignment => String(assignment.sportId) === sportId && Boolean(assignment.positionId)
                  ) ||
                  (sports.length === 1 && Boolean(player.positionId))
              );
              const isComplete = availablePlayers.length > 0 && positionedPlayers.length === availablePlayers.length;
              return (
                <li key={sportId} className={isComplete ? 'is-complete' : ''}>
                  <span>{sport.name || 'Sport'}</span>
                  <b>
                    {isComplete
                      ? 'Positions assigned'
                      : `${positionedPlayers.length}/${availablePlayers.length} positioned`}
                  </b>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {sortedPlayers.length === 0 ? (
        <div className="teamRoster__empty">No participants have been added to this team.</div>
      ) : (
        <section className="teamRoster__participants">
          <header>
            <strong>Participants</strong>
            <span>{sortedPlayers.length}</span>
          </header>
          {sortedPlayers.map(player => {
            const firstName = propz.get(player, ['firstName'], '');
            const lastName = propz.get(player, ['lastName'], '');
            const fullName = `${firstName} ${lastName}`.trim();
            const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
            const avatarUrl = propz.get(player, ['picUrl'], propz.get(player, ['profile', 'picUrl']));
            return (
              <div className="teamRoster__row" key={player.id}>
                <span className="teamRoster__avatar">{avatarUrl ? <img src={avatarUrl} alt="" /> : initials}</span>
                <strong>{fullName}</strong>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
};

SchoolTeamPlayersSummary.displayName = 'SchoolTeamPlayersSummary';
